import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router";
import { useIntl } from "react-intl";
import { StyleguidePanel } from "./settings/StyleguidePanel";
import { BrandingPanel } from "./settings/BrandingPanel";
import { SettingsCard } from "./settings/SettingsCard";
import { ModelPanel } from "./settings/ModelPanel";
import { StammdatenPanel, STAMMDATEN_TABS } from "./settings/StammdatenPanel";
import type { StammdatenTabId } from "./settings/StammdatenPanel";
import { FragebogenPanel } from "./settings/FragebogenPanel";
import { BulkCvImport } from "./settings/BulkCvImport";
import { HrPromptPanel } from "./settings/HrPromptPanel";
import { Role, useHasRole } from "@/auth/roles";
import {
  DEFAULT_ACCENT,
  DEFAULT_BG,
  DEFAULT_SURFACE,
  useThemePrefs,
} from "@/stores/theme-prefs";

// Systemeinstellungen (gated by requireSystemAccess). Each functional section
// is gated by its own genesis privilege: Branding (write:any:system-settings),
// Stammdaten (write:any:reference-data), Fragebögen (write:any:questionnaire).
// genesis enforces these server-side. The client-side Appearance/theme
// playground, the static Uploads table and the Styleguide show to anyone who
// can reach the surface. The old mock panels (Account / Notifications /
// Privacy / Data / Billing) had no backend and were removed.

interface Preset {
  hex: string;
  name: string;
  sub: string;
}

const PALETTE_PRESETS: Record<"bg" | "surface" | "accent", readonly Preset[]> = {
  bg: [
    { hex: DEFAULT_BG, name: "Pure White", sub: "Default" },
    { hex: "#FAF9F6", name: "Porcelain", sub: "kühles Off-White" },
    { hex: "#FAF7F2", name: "Off-White", sub: "warm, fast weiß" },
    { hex: "#F4EEE3", name: "Cream", sub: "wärmer" },
    { hex: "#EBE2D0", name: "Sandy Beige", sub: "gesetzter" },
    { hex: "#E5DCC7", name: "Hellbeton", sub: "Section: SAVE" },
  ],
  surface: [
    { hex: DEFAULT_SURFACE, name: "Porcelain", sub: "Default" },
    { hex: "#FFFFFF", name: "White", sub: "klar" },
    { hex: "#FAF7F2", name: "Off-White", sub: "warm" },
    { hex: "#F7F1E8", name: "Soft Cream", sub: "sanft" },
    { hex: "#EFE7D8", name: "Warm Paper", sub: "ruhig" },
    { hex: "#F1F3F0", name: "Mist", sub: "neutral" },
  ],
  accent: [
    { hex: DEFAULT_ACCENT, name: "Schwarz", sub: "Default" },
    { hex: "#FA8072", name: "Coral", sub: "freundlich" },
    { hex: "#F45D48", name: "Signal Red", sub: "stark" },
    { hex: "#E9A23B", name: "Amber", sub: "warm" },
    { hex: "#4F8F6B", name: "Sage", sub: "ruhig" },
    { hex: "#3A6EA5", name: "Blue", sub: "klar" },
    { hex: "#7A5C9E", name: "Plum", sub: "tief" },
  ],
};

// `role: null` = visible to anyone who can reach the surface.
const ALL_SECTIONS = [
  { id: "branding", labelId: "settings.sectionBranding", role: Role.WriteSystemSettings },
  { id: "hrPrompt", labelId: "settings.sectionHrPrompt", role: Role.WriteSystemSettings },
  { id: "stammdaten", labelId: "settings.sectionStammdaten", role: Role.WriteReferenceData },
  { id: "fragebogen", labelId: "settings.sectionFragebogen", role: Role.WriteQuestionnaire },
  { id: "roster", labelId: "settings.sectionRoster", role: Role.WriteAnyPerson },
  { id: "model", labelId: "settings.sectionModel", role: null },
  { id: "styleguide", labelId: "settings.sectionStyleguide", role: null },
] as const;

type SectionId = (typeof ALL_SECTIONS)[number]["id"];

export function SettingsPage() {
  const intl = useIntl();
  const canSystemSettings = useHasRole(Role.WriteSystemSettings);
  const canReferenceData = useHasRole(Role.WriteReferenceData);
  const canQuestionnaire = useHasRole(Role.WriteQuestionnaire);
  const canWriteAnyPerson = useHasRole(Role.WriteAnyPerson);
  const roleOk: Record<string, boolean> = {
    [Role.WriteSystemSettings]: canSystemSettings,
    [Role.WriteReferenceData]: canReferenceData,
    [Role.WriteQuestionnaire]: canQuestionnaire,
    [Role.WriteAnyPerson]: canWriteAnyPerson,
  };
  const sections = ALL_SECTIONS.filter((s) => s.role === null || roleOk[s.role]);
  const [activeId, setActiveId] = useState<SectionId | null>(null);
  // Fall back to the first visible section when nothing is selected yet or the
  // selection is no longer available to this user.
  const current = activeId && sections.some((s) => s.id === activeId) ? activeId : sections[0]?.id;
  // Selected reference-data sub-type, driven by the Stammdaten sub-menu.
  const [stammdatenTab, setStammdatenTab] = useState<StammdatenTabId>("organisation");

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/55">
        {intl.formatMessage({ id: "settings.eyebrow", defaultMessage: "Account · Preferences" })}
      </div>
      <h1 className="font-serif text-5xl leading-tight">
        {intl.formatMessage({ id: "settings.heading", defaultMessage: "System settings" })}
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-ink/70">
        {intl.formatMessage({
          id: "settings.intro",
          defaultMessage: "Branding, managed reference data, questionnaires and appearance.",
        })}
      </p>

      <section className="mt-8 grid grid-cols-1 items-start gap-6 @lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)] @lg:gap-8">
        <aside className="space-y-1">
          {sections.map((s) => {
            const active = s.id === current;
            return (
              <Fragment key={s.id}>
                <button
                  onClick={() => setActiveId(s.id)}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[14px] tracking-[0.18em] transition ${
                    active ? "font-medium text-ink" : "text-ink/55 hover:font-medium hover:text-ink"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                      active ? "bg-ink" : "bg-transparent"
                    }`}
                  />
                  {intl.formatMessage({ id: s.labelId })}
                </button>
                {s.id === "stammdaten" && active ? (
                  <div className="mb-1 ml-5 space-y-0.5 border-l border-ink/10 pl-3">
                    {STAMMDATEN_TABS.map((tab) => {
                      const subActive = tab.id === stammdatenTab;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setStammdatenTab(tab.id)}
                          className={`block w-full rounded-md px-3 py-1.5 text-left text-[13px] transition ${
                            subActive ? "font-medium text-ink" : "text-ink/55 hover:text-ink"
                          }`}
                        >
                          {intl.formatMessage({ id: tab.labelId })}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </Fragment>
            );
          })}

          {/* Prototyp tobis: liegt auf eigener Route statt als Sektion, damit der
              Prototyp-Code nicht in diese Datei waechst. Deshalb ein Link. */}
          {canSystemSettings ? (
            <Link
              to="/admin/tobis"
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[14px] tracking-[0.18em] text-ink/55 transition hover:font-medium hover:text-ink"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-transparent" />
              {intl.formatMessage({ id: "settings.sectionTobis", defaultMessage: "Movie" })}
            </Link>
          ) : null}
        </aside>

        <section className="space-y-6">
          {current === "branding" && canSystemSettings ? (
            <>
              <BrandingPanel />
              <AppearancePanel />
            </>
          ) : null}
          {current === "hrPrompt" && canSystemSettings ? <HrPromptPanel /> : null}
          {current === "stammdaten" && canReferenceData ? <StammdatenPanel active={stammdatenTab} /> : null}
          {current === "fragebogen" && canQuestionnaire ? <FragebogenPanel /> : null}
          {current === "roster" && canWriteAnyPerson ? <BulkCvImport /> : null}
          {current === "model" ? <ModelPanel /> : null}
          {current === "styleguide" ? <StyleguidePanel /> : null}
        </section>
      </section>
    </main>
  );
}

function AppearancePanel() {
  const intl = useIntl();
  const [activeColorTarget, setActiveColorTarget] = useState<"bg" | "surface" | "accent">("bg");
  const bgColor = useThemePrefs((s) => s.bgColor);
  const setBgColor = useThemePrefs((s) => s.setBgColor);
  const accentColor = useThemePrefs((s) => s.accentColor);
  const setAccentColor = useThemePrefs((s) => s.setAccentColor);
  const surfaceColor = useThemePrefs((s) => s.surfaceColor);
  const setSurfaceColor = useThemePrefs((s) => s.setSurfaceColor);
  const resetPalette = useThemePrefs((s) => s.resetPalette);

  interface ColorTarget {
    id: "bg" | "surface" | "accent";
    label: string;
    hint: string;
    value: string;
    setValue: (v: string) => void;
    presets: readonly Preset[];
  }

  const colorTargets: readonly ColorTarget[] = [
    {
      id: "bg",
      label: intl.formatMessage({ id: "adminSettings.appearance.bgLabel", defaultMessage: "Background" }),
      hint: intl.formatMessage({ id: "adminSettings.appearance.bgHint", defaultMessage: "Base · Page" }),
      value: bgColor,
      setValue: setBgColor,
      presets: PALETTE_PRESETS.bg,
    },
    {
      id: "surface",
      label: intl.formatMessage({ id: "adminSettings.appearance.surfaceLabel", defaultMessage: "Boxes" }),
      hint: intl.formatMessage({ id: "adminSettings.appearance.surfaceHint", defaultMessage: "Cards · Surfaces" }),
      value: surfaceColor,
      setValue: setSurfaceColor,
      presets: PALETTE_PRESETS.surface,
    },
    {
      id: "accent",
      label: intl.formatMessage({ id: "adminSettings.appearance.accentLabel", defaultMessage: "Accent" }),
      hint: intl.formatMessage({ id: "adminSettings.appearance.accentHint", defaultMessage: "Accent · Buttons, highlights" }),
      value: accentColor,
      setValue: setAccentColor,
      presets: PALETTE_PRESETS.accent,
    },
  ];
  const activeTarget =
    colorTargets.find((target) => target.id === activeColorTarget) ?? colorTargets[0]!;

  return (
    <>
      <SettingsCard
        title={intl.formatMessage({ id: "adminSettings.appearance.title", defaultMessage: "Palette" })}
        sub={intl.formatMessage({
          id: "adminSettings.appearance.sub",
          defaultMessage:
            "Background, surface and accent drive the app. Text is set to black or white automatically.",
        })}
      >
        <div className="grid grid-cols-1 gap-3 @sm:grid-cols-3">
          {colorTargets.map((target) => (
            <ColorPicker
              key={target.id}
              label={target.label}
              hint={target.hint}
              value={target.value}
              onChange={target.setValue}
              selected={activeTarget.id === target.id}
              onSelect={() => setActiveColorTarget(target.id)}
            />
          ))}
        </div>

        <div className="mt-5">
          <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/45">
            {intl.formatMessage(
              { id: "adminSettings.appearance.presetsFor", defaultMessage: "Presets for {label}" },
              { label: activeTarget.label },
            )}
          </div>
          <div className="grid grid-cols-6 gap-2">
            {activeTarget.presets.map((p) => (
              <BgPresetButton
                key={p.hex}
                preset={p}
                active={p.hex.toUpperCase() === activeTarget.value.toUpperCase()}
                onClick={() => activeTarget.setValue(p.hex)}
              />
            ))}
          </div>
        </div>

        {bgColor !== DEFAULT_BG || accentColor !== DEFAULT_ACCENT || surfaceColor !== DEFAULT_SURFACE ? (
          <button
            onClick={resetPalette}
            className="mt-5 text-[10px] tracking-[0.25em] text-ink/55 transition hover:text-accent"
          >
            {intl.formatMessage({ id: "adminSettings.appearance.reset", defaultMessage: "↺ Reset to default" })}
          </button>
        ) : null}
      </SettingsCard>
    </>
  );
}

function ColorPicker({
  label,
  hint,
  value,
  onChange,
  selected = false,
  onSelect,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (next: string) => void;
  selected?: boolean;
  onSelect: () => void;
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);

  // Sync draft when the value prop changes from outside (e.g. preset click).
  // setState in effect is intentional - workshop's exact pattern at line 363-365.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!invalid && draft.toUpperCase() !== value.toUpperCase()) setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function commit(next: string) {
    const normalized = normalizeHex(next);
    if (normalized) {
      setInvalid(false);
      setDraft(normalized);
      if (normalized !== value) onChange(normalized);
    } else {
      setInvalid(true);
    }
  }

  return (
    <div
      onClick={onSelect}
      onFocusCapture={onSelect}
      data-selected={selected ? "true" : undefined}
      className={`block cursor-pointer rounded-md p-4 ring-1 transition ${
        selected
          ? "bg-ink/[0.06] ring-accent"
          : "bg-surface ring-ink/10 hover:bg-ink/[0.06] hover:ring-accent/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <label
          className="relative h-10 w-10 shrink-0 cursor-pointer overflow-hidden rounded-md ring-1 ring-ink/10"
          style={{ background: value }}
          title={intl.formatMessage({ id: "adminSettings.appearance.openPicker", defaultMessage: "Open colour picker" })}
        >
          <input
            type="color"
            value={value}
            onChange={(e) => {
              setDraft(e.target.value.toUpperCase());
              onChange(e.target.value);
            }}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={label}
          />
        </label>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{label}</span>
          </div>
          <div className="text-[11px] text-ink/55">{hint}</div>
        </div>
        <input
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setInvalid(false);
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(draft);
              e.currentTarget.blur();
            }
            if (e.key === "Escape") {
              setDraft(value);
              setInvalid(false);
              e.currentTarget.blur();
            }
          }}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          placeholder="#RRGGBB"
          className={`w-24 rounded bg-surface px-2 py-1 text-right font-mono text-[11px] tabular-nums ring-1 focus:outline-none ${
            invalid ? "ring-red-500/60" : "ring-ink/10 focus:ring-ink/30"
          }`}
        />
      </div>
    </div>
  );
}

function normalizeHex(input: string): string | null {
  let s = String(input).trim().replace(/^#/, "");
  if (s.length === 3) s = s.split("").map((c) => c + c).join("");
  if (/^[0-9a-f]{6}$/i.test(s)) return "#" + s.toUpperCase();
  return null;
}

function BgPresetButton({
  preset,
  active,
  onClick,
}: {
  preset: Preset;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`${preset.name} · ${preset.hex}`}
      className={`rounded-md bg-surface p-2 text-left ring-1 transition ${
        active ? "ring-accent" : "ring-ink/10 hover:ring-accent/40"
      }`}
    >
      <span
        className="block aspect-[3/1] w-full rounded-sm ring-1 ring-ink/5"
        style={{ background: preset.hex }}
      />
      <div className="mt-2 text-[10px] tracking-[0.18em] text-ink/70">{preset.name}</div>
      <div className="font-mono text-[9px] tabular-nums text-ink/45">{preset.hex}</div>
      <div className="text-[9px] leading-tight text-ink/40">{preset.sub}</div>
    </button>
  );
}
